import { createFileRoute, useNavigate } from '@tanstack/react-router';
import parc_logo from '@assets/parc.png';
import { Button, VStack, HStack, textVariants, Text, Link } from 'ui_2';
import {
  BiChevronLeft,
  BiLogoDiscordAlt,
  BiLogoTelegram,
  BiLogoTwitter,
  BiLogoYoutube,
} from 'react-icons/bi';
import { trpc } from '@src/data/trpc';
import { Network } from '@puzzlehq/sdk';
import { LoadingMessage } from '@src/components/LoadingMessage';
import { ErrorMessage } from '@src/components/ErrorMessage';
import { useQuestProgress } from '@src/hooks/useQuests';
import { Route as IndexRoute } from '../index';
import { useDappState } from '@src/hooks/useDappState';
import { getDeeplinkBrowserUrl } from '@puzzlehq/frontend-utils';
import { useViewTransition } from '@src/hooks/useViewTransition';

export const Route = createFileRoute('/quest/$questId')({
  staticData: {
    title: 'Quest',
  },
  loader: ({ params }) => {
    return trpc.getQuest.query({
      network: import.meta.env.VITE_NETWORK! as Network,
      client: 'extension',
      questId: params.questId,
    });
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.bentoTitle
          ? `Quest: ${loaderData?.bentoTitle}`
          : 'Quest',
      },
    ],
  }),
  errorComponent: function Component({ error }) {
    return <ErrorMessage error={error} />;
  },
  pendingComponent: function Component() {
    const { questId } = Route.useParams();
    return <LoadingMessage message={`Loading QuestId ${questId}`} />;
  },
  component: RouteComponent,
});

// Social icon components (simplified - you might want to use react-icons)
const SocialIcons = {
  youtube: () => (
    <BiLogoYoutube className='transition-colors duration-300 group-hover:text-[#ff0033] group-active:text-[#ff0033]' />
  ),
  discord: () => (
    <BiLogoDiscordAlt className='transition-colors duration-300 group-hover:text-[#5865F2] group-active:text-[#5865F2]' />
  ),
  x: () => (
    <BiLogoTwitter className='transition-colors duration-300 group-hover:text-[#000000] group-active:text-[#000000]' />
  ),
  telegram: () => (
    <BiLogoTelegram className='transition-colors duration-300 group-hover:text-[#28a9e9] group-active:text-[#28a9e9]' />
  ),
};

function RouteComponent() {
  const { questId } = Route.useParams();
  const questData = Route.useLoaderData();
  const { data: progressData, isSuccess } = useQuestProgress(questId);
  const progressPercentage = isSuccess
    ? (progressData.dailyEarnings / progressData.dailyLimit) * 100
    : undefined;

  const navigate = useNavigate({ from: Route.fullPath });

  const { isPuzzleWalletWebview } = useDappState();
  const { slideRightFullPage } = useViewTransition();

  const _dappUrl = isPuzzleWalletWebview
    ? getDeeplinkBrowserUrl(questData.url, 'rootNavigationFlow')
    : questData.url;

  const dappUrl =
    questData.questType === 'onramp'
      ? isPuzzleWalletWebview
        ? `https://app.puzzle.online/buy`
        : 'https://coinmarketcap.com/currencies/aleo/#Markets'
      : _dappUrl;

  return (
    <div className='h-dvh overflow-y-auto bg-white'>
      {/* Header with close button */}
      <div className='relative'>
        <button
          className='pt-safe absolute left-4 top-4 z-20 flex h-12 w-12 items-center justify-center p-2 text-white md:right-6 md:top-6'
          onClick={() =>
            navigate({
              to: IndexRoute.to,
              viewTransition: isPuzzleWalletWebview
                ? slideRightFullPage
                : undefined,
              search: {
                tab: 'home',
              },
            })
          }
        >
          <BiChevronLeft size={30} />
        </button>

        {/* Hero Section with configurable branding */}
        <div className='relative bg-black text-white'>
          {/* Background image will go here */}
          {questData.backgroundSplashImageURL && (
            <div className='pt-safe absolute inset-0'>
              <img
                src={questData.backgroundSplashImageURL}
                className='h-full w-full object-cover'
              />
            </div>
          )}

          <VStack className='relative z-10 w-full items-center px-6 pb-8 pt-[120px] md:px-12 md:pb-16 md:pt-32 lg:px-20 lg:pt-40'>
            {/* Earn section - moved down to give space for branding */}
            {questData.logoURL && (
              <img src={questData.logoURL} className='h-[30px] md:h-[40px]' />
            )}
            <VStack className='mx-auto mt-16 w-full max-w-4xl gap-6 text-center md:mt-20 md:gap-8 lg:mt-24'>
              <VStack className='gap-2 md:gap-4'>
                <HStack className='items-center justify-center gap-3 md:gap-4'>
                  <span className='font-body text-4xl font-bold md:text-5xl lg:text-6xl'>
                    Earn
                  </span>
                  <HStack className='items-center gap-2 rounded-full bg-yellow-50 px-4 py-2 text-yellow-800 md:gap-3 md:px-6 md:py-3'>
                    <span className='body font-body text-2xl font-medium md:text-3xl lg:text-4xl'>
                      {questData.mintAmount}
                    </span>
                    <img
                      src={parc_logo}
                      className='h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7'
                      alt='PARC'
                    />
                  </HStack>
                </HStack>
                <div className='font-body text-4xl font-bold md:text-5xl lg:text-6xl'>
                  for each {questData.action}
                </div>
              </VStack>

              {/* Progress bar */}
              {isSuccess && (
                <VStack className='gap-3 md:gap-4'>
                  <div className='h-3 w-full overflow-hidden rounded-full bg-gray-700 md:h-4'>
                    <div
                      className='h-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 shadow-sm transition-all duration-500 ease-out md:h-4'
                      style={{
                        width: `${progressPercentage}%`,
                      }}
                    />
                  </div>
                  <HStack className='justify-between text-sm text-gray-300 md:text-base'>
                    {progressData.isPro ? (
                      <Text font={'sm'} color={'white'}>
                        <span
                          className={textVariants({
                            font: 'bold-sm',
                            color: 'white',
                          })}
                        >
                          Pro Deal
                        </span>{' '}
                        {questData.dailyProLimit} {questData.action}s a day
                      </Text>
                    ) : (
                      <></>
                    )}
                    <span className='font-medium'>
                      {progressData.dailyEarnings}/{progressData.dailyLimit}{' '}
                      PARC
                    </span>
                  </HStack>
                </VStack>
              )}

              {/* Start Quest Button */}
              <Button
                fullWidth
                className='max-w-[350px] self-center'
                onClick={() => window.open(dappUrl, '_blank')}
              >
                Start Quest
              </Button>

              {/* Description */}
              {/* <Text font={'sm'} color={'white'}>
                <span
                  className={textVariants({ font: 'bold-sm', color: 'white' })}
                >
                  Pro Rewards
                </span>{' '}
                Gas covered 24/7
              </Text> */}
              {isPuzzleWalletWebview && (
                <Link variant='white' href='https://app.puzzle.online/paywall'>
                  Get Puzzle Pro to unlock {questData.dailyProLimit}{' '}
                  {questData.action}s
                </Link>
              )}
            </VStack>
          </VStack>
        </div>
      </div>

      {/* Content Section */}
      <VStack className='mx-auto max-w-4xl gap-8 px-6 py-8 md:gap-12 md:px-12 md:py-16 lg:px-20'>
        {/* Quest Instructions Header */}
        <h1 className='text-2xl font-semibold leading-tight text-gray-900 md:text-3xl lg:text-4xl'>
          {questData.instructionsHeader}
        </h1>

        <div className='grid gap-8 md:gap-12 lg:grid-cols-3 lg:gap-16'>
          {/* Additional Resources */}
          {questData.socials && Object.keys(questData.socials).length > 0 && (
            <VStack className='gap-4 md:gap-6 lg:col-span-1 lg:gap-4'>
              <h2 className='text-base font-medium text-gray-700 md:text-lg'>
                Additional resources
              </h2>
              <div className='flex flex-wrap gap-4 md:flex-col md:gap-6 lg:gap-4'>
                {questData.socials.youtube && (
                  <a
                    href={questData.socials.youtube}
                    className='group flex items-center gap-2 text-sm text-gray-600 transition-colors md:text-base'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='text-xl md:text-2xl'>
                      {SocialIcons.youtube()}
                    </div>
                    <span>Youtube</span>
                  </a>
                )}
                {questData.socials.discord && (
                  <a
                    href={questData.socials.discord}
                    className='group flex items-center gap-2 text-sm text-gray-600 transition-colors md:text-base'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='text-xl md:text-2xl'>
                      {SocialIcons.discord()}
                    </div>
                    <span>Discord</span>
                  </a>
                )}
                {questData.socials.x && (
                  <a
                    href={questData.socials.x}
                    className='group flex items-center gap-2 text-sm text-gray-600 transition-colors md:text-base'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='text-xl md:text-2xl'>{SocialIcons.x()}</div>
                    <span>X</span>
                  </a>
                )}
                {questData.socials.telegram && (
                  <a
                    href={questData.socials.telegram}
                    className='group flex items-center gap-2 text-sm text-gray-600 transition-colors md:text-base'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='text-xl md:text-2xl'>
                      {SocialIcons.telegram()}
                    </div>
                    <span>Telegram</span>
                  </a>
                )}
              </div>
            </VStack>
          )}

          {/* instructions */}
          {questData.instructions && (
            <VStack
              className={`gap-6 md:gap-8 ${
                questData.socials ? 'lg:col-span-2' : 'lg:col-span-3'
              }`}
            >
              <VStack className='gap-6 pb-4 md:gap-8'>
                {questData.instructions?.map((step, index) => (
                  <VStack key={index} className='gap-3 md:gap-4'>
                    <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-500 md:text-base'>
                      Step {index + 1}
                    </h3>
                    <p className='text-base leading-relaxed text-gray-900 md:text-lg md:leading-relaxed'>
                      {step}
                    </p>
                  </VStack>
                ))}
              </VStack>
            </VStack>
          )}
        </div>
      </VStack>
    </div>
  );
}
